import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { Divider, Grid, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { fontLink, fullCenter, greenButton } from "../../base";
import ArrowCircleRightRoundedIcon from '@mui/icons-material/ArrowCircleRightRounded';
import LangLink from "../../Localization/LangLink";
import { publicBarNavigation } from "../Public/AppBar/AppBar";
import { Txt } from "../../Routes/Localized";
import LanguageMenu from "../Public/AppBar/LanguageMenu";
import { languagesMenu } from "../Public/CountriesLayout/CountriesLayout";
import { useLanguage } from "../../Localization/LanguageLayout";
import { setStorageLang } from "../../Localization/routesLocalized";
import i18n from "../../Localization/i18n";
import { ReactComponent as IconLight } from './icon_light.svg';
import { ReactComponent as IconRight } from './icon_right.svg';
import { useMobileMenu } from "./MobileMenuProvider";
import VinButton from "../Public/Header/VinButton";
import { colorBlueDark, colorGrey, colorLogo } from "../../Resources/Design/theme";

const ANIMATION_DURATION_MS = 200;

export default function MobileDrawerMenu({
    children,
    showPublicNav = true,
}) {
    const { setLangOpenInternal: setLangOpen, setOpenInternal: setOpen, open, langOpen } = useMobileMenu();
    const [visible, setVisible] = useState(open);

    const [overflowing, setOverflowing] = useState(false);
    const language = useLanguage();
    const location = useLocation();
    // Do not useLangNavigate here
    const navigate = useNavigate();
    const anyOutlined = publicBarNavigation.find((data) => location.pathname.includes(data.link));

    const paperRef = useRef(null);

    const checkOpen = () => {
        const el = paperRef.current;
        const rect = el.getBoundingClientRect();

        if (open && rect.top < -500) {
            setOpen(false);
            setTimeout(() => {
                setOpen(true);
            }, 100);
        }
    }

    useEffect(() => {
        if (open) {
            setOpen(false);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (open) {
            setTimeout(checkOpen, 100);
            setVisible(true);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;

        let raf;

        const check = () => {
            const el = paperRef.current;

            if (el) {
                const rect = el.getBoundingClientRect();
                const style = window.getComputedStyle(el);

                console.log(
                    'DRAWER',
                    'open=', open,
                    'visible=', visible,
                    'transform=', style.transform,
                    'top=', rect.top,
                    'height=', rect.height
                );
            }

            raf = requestAnimationFrame(check);
        };

        raf = requestAnimationFrame(check);

        return () => cancelAnimationFrame(raf);
    }, [open, visible]);

    useEffect(() => {
        if (!paperRef.current) return;
        const el = paperRef.current;

        const observer = new ResizeObserver(() => {
            if (!el) return;

            const isOverflowingY = el.scrollHeight > el.clientHeight;
            const scrollbarWidth = el.offsetWidth - el.clientWidth;

            setOverflowing(prev => {
                const next = isOverflowingY ? scrollbarWidth : 0;
                return prev !== next ? next : prev;
            });
        });

        observer.observe(el);

        return () => observer.disconnect();
    }, []);

    const mxMobile = 16;
    const mxTablet = 24;
    const mx = {
        ml: { xs: `${mxMobile}px`, sm: `${mxTablet}px`, md: `${mxTablet}px` },
        mr: { xs: `${mxMobile - overflowing}px`, sm: `${mxTablet - overflowing}px`, md: `${mxTablet - overflowing}px` },
    };

    const list = () => (
        <Box
            sx={{
                mt: { xs: '80px', sm: '80px', md: '100px' },
                width: "auto", bgcolor: "white",
                ...mx,
            }}
            role="presentation"
            onClick={() => {
                setOpen(false);
            }}
        >
            {Boolean(showPublicNav) && publicBarNavigation.map((data, index) => (
                <Box
                    key={"PhoneNav" + index}
                    sx={{
                        height: `50px`, width: '100%', bgcolor: "#F8F8FC",
                        borderRadius: '10px', boxShadow: 'none',
                        border: `2px solid transparent`,
                        ...(Boolean(anyOutlined ? location.pathname.includes(data.link) : index === 0) && {
                            border: '2px solid #0165F4',
                        }),
                        ...(index !== 0 && { mt: '8px' }),
                        WebkitTapHighlightColor: "transparent",
                        userSelect: 'none',
                        "&:active": {
                            border: '2px solid #0165F4',
                        },
                    }}>
                    <Box
                        component={LangLink}
                        to={data.link}
                        sx={{
                            width: '100%', height: '100%', px: { xs: '16px', sm: '16px', md: '30px' },
                            display: 'flex', flexDirection: 'row', alignItems: 'center', alignContent: 'center',
                            textDecoration: 'none',
                        }}>
                        <Typography sx={{ fontWeight: 'bold', ...fontLink }}>
                            {data.text}
                        </Typography>
                        <Box sx={{ ml: 'auto' }}>
                            {Boolean(location.pathname.includes(data.link)) ?
                                <IconLight style={{ display: 'block', width: '24px', height: '24px' }} /> :
                                <IconRight style={{ display: 'block', width: '24px', height: '24px' }} />}
                        </Box>
                    </Box>
                </Box>
            ))}

            <Box sx={{ ...fullCenter, flexDirection: 'column', mt: '8px', mb: '8px', width: '100%' }}>
                <VinButton
                    headerView
                    hideEndAdornment
                    onSuccess={() => setOpen(false)}
                    sx={{
                        border: `2px solid ${colorLogo}`,
                        borderRadius: '10px',
                        height: '47px',
                    }}
                    sxInput={{
                        py: '8px',
                        pl: '0px',
                        mx: 'auto',
                        textAlign: 'center',
                        '& .MuiInputBase-input': {
                            textAlign: 'center',
                            color: colorBlueDark,
                        },
                        '& .MuiInputBase-input::placeholder': {
                            opacity: 1,
                            color: `#686767 !important`,
                        },
                    }}
                    sxButton={{
                        borderRadius: '10px',
                        height: '47px',
                    }}
                />
                {/*<Box component={LangLink} to={`cart/search`} sx={{ cursor: 'pointer', textDecoration: 'none', width: 'fit-content' }}>
                    <div style={{ userSelect: 'none', display: 'block' }} >
                        <Box sx={{ ...greenButton, borderRadius: '50px', height: '40px', ...fullCenter, }}>
                            <Typography sx={{
                                fontFamily: 'inter', textWrap: 'nowrap', fontWeight: 700, ...fontLink, maxWidth: '400px', px: '15px', color: '#fff',
                            }}>
                                <Txt id={"10024"} />
                            </Typography>
                        </Box>
                    </div>
                </Box>*/}

                <Box
                    component={LangLink} sx={{ textDecoration: 'none', mt: '16px' }}
                    to={"find_report"}
                >
                    <Typography
                        sx={{
                            fontFamily: 'inter', textWrap: 'nowrap', fontWeight: 700, ...fontLink, p: '10px',
                        }}>
                        <Txt id="121012" />
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ ...fullCenter, flexDirection: 'column', mb: '34px' }}>
                {Boolean(showPublicNav) && <LanguageMenu setOpenExtern={setLangOpen} openExtern={langOpen} sxInside={{ p: '16px' }} />}
            </Box>

        </Box>
    );

    const langList = () => (
        <Box
            sx={{
                mt: { xs: '80px', sm: '80px', md: '100px' },
                width: "auto", bgcolor: "white",
                pb: '32px',
                ...mx,
            }}
            role="presentation"
        >
            <Grid container columns={24} spacing={2}>
                {languagesMenu.map((lang) => (
                    <Grid key={"GridFlag" + lang.code} item xs={12}>
                        <Box
                            key={"GridBox" + lang.code}
                            onClick={(e) => {
                                if (language !== lang.code) {
                                    setStorageLang(lang.code);
                                    i18n.changeLanguage(lang.code).then((result) => {
                                        setTimeout(() => {
                                            window?.updateSvgBoxes();
                                            window?.updateAiAgentSvgBoxes();
                                        }, 0);
                                    });

                                    const restOfPath = window.location.pathname.split("/").slice(2).join("/");
                                    navigate(`/${lang.code}/${restOfPath}`);
                                }
                                setOpen(false);
                            }}
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                alignContent: 'center',
                                borderRadius: "10px",
                                backgroundColor: '#F8F8FC',
                                border: "2px solid #F8F8FC",
                                cursor: 'pointer',
                                ...(language === lang.code && {
                                    border: "2px solid #0165F4"
                                }),
                                py: '8px',
                                pl: '16px'
                            }}
                        >
                            <Box
                                component="img"
                                src={lang.flag}
                                alt={lang.code}
                                sx={{
                                    width: '22px',
                                    height: '22px',
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    border: '1px solid #eee',
                                }}
                            />

                            <Box sx={{ pl: '16px' }}>
                                <Typography fontWeight={600} sx={{ ...fontLink }}>
                                    {lang.label}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {lang.sub}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    return (
        <div>
            <React.Fragment key={"TopDrawerMenu"}>
                {children}
                <SwipeableDrawer
                    transitionDuration={ANIMATION_DURATION_MS}
                    anchor={"top"}
                    open={open}
                    disableScrollLock={true}
                    onClose={(e) => {
                        setOpen(false);
                    }}
                    onOpen={() => {
                        setVisible(true);
                        setOpen(true);
                    }}
                    SlideProps={{
                        appear: true,
                        mountOnEnter: true,
                        unmountOnExit: true,
                        onExited: () => {
                            if (!open) {
                                setVisible(false);
                            }
                        },
                    }}
                    slotProps={{
                        backdrop: {
                            invisible: true,
                            sx: {
                                pointerEvents: 'auto',
                            },
                        }
                    }}
                    sx={{
                        //display: "flex",
                        visibility: visible ? 'visible' : 'hidden',
                    }}
                    disableSwipeToOpen
                    PaperProps={{
                        ref: paperRef,
                        sx: {
                            minWidth: '340px',
                            zIndex: (theme) => theme.zIndex.appBar - 1,
                        }
                    }}
                >
                    {langOpen ? langList() : list()}
                </SwipeableDrawer>
            </React.Fragment>
        </div>
    );
}
